import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import Placeholder from "components/Layout/Placeholder";
import RecordLabel from "components/RecordLabel";
import { getUserData } from "containers/Auth/Auth.selectors";
import { getActiveBundleId, getBundles } from "containers/Bundles/Bundles.selectors";
import { Bundle, Project, Workspace } from "containers/ExecutionFilter/interfaces";
import {
    getActiveProjectId,
    getDefaultProjectId,
    getProjects,
} from "containers/Projects/Projects.selectors";
import { MenuParent } from "containers/Projects/components/ProjectTree/createMenuParent";
import {
    getSortedList,
    quickAccessOpened,
    quickAccessPinned,
} from "containers/Projects/components/ProjectTree/utils";
import { ProjectQuickAccessEntry } from "containers/Projects/components/ProjectTree/interfaces";
import { resourceDataSelector } from "containers/Resources/Resources.selectors";
import { getWorkspaceWithQuickAccess } from "containers/Workspaces/Workspaces.selectors";
import qs from "qs";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Menu, MenuItem, ProSidebar } from "react-pro-sidebar";
import { useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { bundleManagerPathFragment } from "utils/RouterManager/constants";
import { useLocation } from "react-router-dom";
import { isContainerNavigationLinkActive } from "utils/RouterManager/subPageLinks";
import { mapScreensToPaths, useMatchExecutionFilterLocation } from "utils/RouterManager/utils";
import { getIri } from "utils/utils";
import "./ProjectTree.scss";

export const propagateScrollToSidebar = "propagateScrollToSidebar";

interface ProjectRefs {
    [key: string]: HTMLSpanElement;
}

const quickAccessListsFor = (container: {
    [key: string]: ProjectQuickAccessEntry[] | undefined;
}) => [...(container[quickAccessOpened] ?? []), ...(container[quickAccessPinned] ?? [])];

const containerHasQuickAccessRecord = (
    container: { id: string; [key: string]: ProjectQuickAccessEntry[] | undefined },
    recordRef: string
) =>
    quickAccessListsFor(container).some(
        (entry) =>
            entry.record_id_record.id === recordRef || entry.record_id_record.name === recordRef
    );

const ProjectTree = () => {
    const history = useHistory();

    const projectRefs = useRef<ProjectRefs>({});
    const [openState, setOpenState] = useState<{ [key: string]: boolean }>({});

    const match = useRouteMatch("/:resourceName");
    const resourceName = (match?.params as any)?.resourceName;
    const matchRecordSubPath = useRouteMatch<{ pathRecordId: string }>(
        "/:resourceType/:pathRecordId/:subPath"
    );
    const queryParams = qs.parse(window?.location?.search.substring(1));
    const recordId =
        queryParams?.id ?? queryParams?.name ?? matchRecordSubPath?.params?.pathRecordId;

    const iri = getIri(resourceName, recordId);
    const resourceData = useSelector(resourceDataSelector(iri));
    const isOpenedRecordDeleted = resourceData?.deleted_at;

    const userData = useSelector(getUserData);
    const workspaceID = userData?.workspace_id;
    const defaultProjectId = useSelector(getDefaultProjectId);
    const activeProjectId = useSelector(getActiveProjectId);
    const activeBundleId = useSelector(getActiveBundleId);
    const location = useLocation();
    const currentQueryParams = qs.parse(location.search.substring(1));
    // bundle-manager is now a query-param sub-page; keep variable name for downstream consumers
    const matchBundleManagerPage = bundleManagerPathFragment in currentQueryParams ? match : null;
    const matchTrashPage = useRouteMatch(mapScreensToPaths.trash);
    const isProjectOrBundleType = resourceName === "project" || resourceName === "bundle";
    const isEntryInsideWorkspace = document.querySelector('a[class*="breadcrumbs"');

    const projects: Project[] = useSelector(getProjects);
    const bundles: Bundle[] = useSelector(getBundles);
    const workspace: Workspace = useSelector(getWorkspaceWithQuickAccess);

    // Memoize the derived sorted lists: `getSortedList` returns a BRAND-NEW array
    // of new objects on every call, so computing it inline on each render gave
    // `sortedProjects`/`sortedBundles` a fresh identity every render. That made the
    // `dataset.id`/onclick-setting effect below (and the quickAccess/containerSelf
    // memos) re-run on EVERY render — an imperative DOM-attribute rewrite of every
    // sidebar node on each of the many store dispatches develop's live subscription
    // produces (a dominant idle-CPU loop, esp. while the integration suite churns
    // temp projects). `projects`/`bundles` are stable reducer references while the
    // list is unchanged, so this recomputes exactly when the list actually changes.
    // (operator-task-operator-dashboard-cpu-render-loop)
    const sortedProjects = useMemo(() => getSortedList(projects), [projects]);
    const sortedBundles = useMemo(() => getSortedList(bundles), [bundles]);

    const activeWorkspaceId =
        (resourceData?.bundle_id === null &&
            resourceData?.project_id === null &&
            !isProjectOrBundleType) ||
        isEntryInsideWorkspace
            ? workspaceID
            : null;

    const quickAccessParentId = useMemo(() => {
        if (!recordId || activeProjectId || activeBundleId || activeWorkspaceId) {
            return null;
        }
        for (const project of sortedProjects ?? []) {
            if (containerHasQuickAccessRecord(project, recordId as string)) {
                return project.id;
            }
        }
        for (const bundle of sortedBundles ?? []) {
            if (containerHasQuickAccessRecord(bundle, recordId as string)) {
                return bundle.id;
            }
        }
        if (containerHasQuickAccessRecord(workspace, recordId as string)) {
            return workspaceID;
        }
        return null;
    }, [
        activeBundleId,
        activeProjectId,
        activeWorkspaceId,
        recordId,
        sortedBundles,
        sortedProjects,
        workspace,
        workspaceID,
    ]);

    // When viewing a container's own sub-page (e.g. bundle logs, project canvas), highlight that
    // container in the sidebar. Neither activeProjectId/activeBundleId (Redux, never dispatched
    // on navigation) nor quickAccessParentId (only searches records INSIDE containers) cover this.
    const containerSelfId = useMemo(() => {
        if (!isProjectOrBundleType || !recordId) return null;
        if (resourceName === "bundle") {
            return sortedBundles.find((b) => b.name === recordId || b.id === recordId)?.id ?? null;
        }
        if (resourceName === "project") {
            return (
                sortedProjects.find((p) => p.name === recordId || p.id === recordId)?.id ?? null
            );
        }
        return null;
    }, [isProjectOrBundleType, recordId, resourceName, sortedBundles, sortedProjects]);

    const activeId =
        matchBundleManagerPage || matchTrashPage || isOpenedRecordDeleted
            ? null
            : activeProjectId ?? activeBundleId ?? activeWorkspaceId ?? containerSelfId ?? quickAccessParentId;
    const [previousActiveId, setPreviousActiveId] = useState<string | null>(null);

    const { matchExecutionFilterPage } = useMatchExecutionFilterLocation();

    const handleClick = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const id = e.currentTarget.dataset.id;
            const newOpenState = { ...openState };
            newOpenState[id] = !newOpenState[id];
            setOpenState(newOpenState);
        },
        [openState]
    );

    useEffect(() => {
        if (projectRefs.current) {
            for (const [key, value] of Object.entries(projectRefs.current)) {
                if (value?.firstChild?.lastChild) {
                    (value?.firstChild?.lastChild as HTMLElement).dataset.id = key;
                    (value?.firstChild?.lastChild as HTMLElement).onclick = function (e) {
                        handleClick(e);
                    };
                }
            }
        }
    }, [handleClick, sortedProjects, sortedBundles]);

    // open the currently selected container in the sidebar
    useEffect(() => {
        if (!activeId || activeId === previousActiveId) {
            return;
        }

        if (isOpenedRecordDeleted !== undefined) {
            setOpenState((prevState) => {
                // initial state when deleted record opened
                if (!Object.keys(prevState).length && isOpenedRecordDeleted) {
                    return {};
                } else {
                    prevState[activeId] = true;
                    return prevState;
                }
            });
            setPreviousActiveId(activeId);
        } else {
            // Prefer the container whose own sub-page link is active (e.g. bundle logs,
            // project canvas — those carry the `activePageLink` class). Fall back to
            // `activeId` (the container resolved via containerSelfId/quickAccessParentId)
            // so a container holding the active NESTED RECORD row is still expanded — that
            // row carries the `selected` class, NOT `activePageLink`, so without this the
            // submenu never opens and RecordLabel's scroll-to-current can't reach it
            // (e.g. a pinned dashboard opened at /dashboard?name=…&view).
            const containerIdWithActivePage = Object.entries(projectRefs.current ?? {}).find(
                ([id, el]) => el?.getElementsByClassName("activePageLink")?.length > 0
            )?.[0];

            const containerToOpen = containerIdWithActivePage ?? activeId;
            if (containerToOpen) {
                setOpenState((prevState) => ({ ...prevState, [containerToOpen]: true }));
                setPreviousActiveId(activeId);
            }
        }
    }, [isOpenedRecordDeleted, sortedProjects, sortedBundles, previousActiveId, activeId]);

    const handleScrollPropagation = (event) => {
        const scrollableSidebar = document.querySelector(".sidebar__bottom");
        if (scrollableSidebar) {
            scrollableSidebar.scrollTo({
                top: scrollableSidebar.scrollTop + event.deltaY,
                behavior: "smooth",
            });
        }
    };

    useEffect(() => {
        const attachWheelEventToTooltips = () => {
            const tooltips = document.querySelectorAll(
                ".propagateScrollToSidebar .rc-tooltip-inner"
            );
            tooltips.forEach((tooltip) => {
                tooltip.addEventListener("wheel", handleScrollPropagation, { passive: true });
            });
        };

        const detachWheelEventFromTooltips = () => {
            const tooltips = document.querySelectorAll(
                ".propagateScrollToSidebar .rc-tooltip-inner"
            );
            tooltips.forEach((tooltip) => {
                tooltip.removeEventListener("wheel", handleScrollPropagation);
            });
        };

        document.addEventListener("wheel", attachWheelEventToTooltips);

        return () => {
            document.addEventListener("wheel", detachWheelEventFromTooltips);
        };
    }, []);

    const props = {
        defaultProjectId,
        workspaceID,
        setPreviousActiveId,
        activeId,
        matchExecutionFilterPage,
        matchBundleManagerPage,
        matchTrashPage,
        isOpenedRecordDeleted,
        projectRefs,
        openState,
        setOpenState,
        recordId,
    };
    const createMenuContainer = (resource, resourceType, className) => (
        <MenuParent
            key={resource.id}
            resource={resource}
            resourceType={resourceType}
            className={className}
            {...props}
        />
    );

    const treeComponent = (
        <ProSidebar data-testid="projectTree">
            <Menu iconShape="round">
                <MenuItem className="bundlesHeadline projectsHeadline">
                    <div className="headline text-dark-grey text-sm text-uppercase">Workspace</div>
                    <hr />
                </MenuItem>
                <Fragment key="workspace">
                    {createMenuContainer(workspace, "workspace", "workspaceEntry")}
                </Fragment>

                <MenuItem className="bundlesHeadline projectsHeadline headline-space">
                    <div className="headline text-dark-grey text-sm text-uppercase">Projects</div>
                    <hr />
                </MenuItem>

                <Fragment key="projects">
                    {sortedProjects?.map((project) => createMenuContainer(project, "project", ""))}
                </Fragment>

                <MenuItem className="bundlesHeadline projectsHeadline headline-space">
                    <div className="headline text-dark-grey text-sm text-uppercase">Bundles</div>
                    <hr />
                </MenuItem>

                <MenuItem key="bundle-manager" className="pro-sub-menu bundle-manager-entry">
                    <RecordLabel
                        name="Bundle Manager"
                        link={mapScreensToPaths.bundleManager(workspaceID)}
                        icon={IconCollection.cartArrowDown}
                        isSelectedManually={isContainerNavigationLinkActive(
                            mapScreensToPaths.bundleManager(workspaceID),
                            location.pathname,
                            location.search
                        )}
                        scrollIntoView
                        tooltipOverlayClassName={propagateScrollToSidebar}
                    />
                    {!sortedBundles.length && (
                        <div className="emptyProjectPlaceholder">
                            <Icon icon={IconCollection.packageVariant} />
                            <div className="text">
                                Use the{" "}
                                <button
                                    type="button"
                                    onClick={() => {
                                        history.push(mapScreensToPaths.bundleManager(workspaceID));
                                    }}
                                >
                                    Bundle Manager
                                </button>{" "}
                                to download and install bundles
                            </div>
                        </div>
                    )}
                </MenuItem>

                <Fragment key="bundles">
                    {sortedBundles?.map((bundle) => createMenuContainer(bundle, "bundle", ""))}
                </Fragment>

                <MenuItem className="bundlesHeadline projectsHeadline headline-space">
                    <hr />
                </MenuItem>
                <MenuItem className="deletedEntry" active={isOpenedRecordDeleted}>
                    <RecordLabel
                        name="Trash"
                        link={mapScreensToPaths.trash}
                        icon={IconCollection.deleteOutline}
                        tooltipOverlayClassName={propagateScrollToSidebar}
                    />
                </MenuItem>
            </Menu>
        </ProSidebar>
    );

    return (
        <div className="projectTree">
            {!sortedProjects.length && !sortedBundles.length ? <Placeholder.List /> : treeComponent}
        </div>
    );
};

export default ProjectTree;
